/* global axios */
import store from 'dashboard/store';

const sortInboxesLikeSidebar = inboxes =>
  [...inboxes].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, {
      sensitivity: 'base',
    })
  );

/**
 * Fetch inboxes with an explicit account id (never hardcode inbox ids).
 * Avoids /api/v1/accounts//cache_keys when window.location has no account yet.
 */
const fetchInboxesForAccount = async accountId => {
  if (!accountId) return [];

  const cached = store.getters['inboxes/getInboxes'] || [];
  if (cached.length) return cached;

  try {
    const { data } = await axios.get(`/api/v1/accounts/${accountId}/inboxes`);
    const payload = data?.payload || [];
    if (Array.isArray(payload) && payload.length) {
      try {
        store.commit('inboxes/SET_INBOXES', payload);
      } catch (_e) {
        // ignore store commit failures
      }
      return payload;
    }
  } catch (_e) {
    // fall through
  }

  try {
    await store.dispatch('inboxes/get');
  } catch (_e) {
    // ignore
  }
  return store.getters['inboxes/getInboxes'] || [];
};

/** First channel in the sidebar (alphabetical by name — same as menu). */
export const getFirstSidebarInbox = async (accountId = null) => {
  const id =
    accountId ||
    store.getters.getCurrentAccountId ||
    store.getters.getCurrentUser?.account_id;

  const inboxes = await fetchInboxesForAccount(id);
  if (!inboxes.length) return null;
  return sortInboxesLikeSidebar(inboxes)[0];
};

const ensureCurrentUserLoaded = async () => {
  const user = store.getters.getCurrentUser;
  if (user?.id && (user.accounts || []).length) return;
  try {
    await store.dispatch('setUser');
  } catch (_e) {
    try {
      await store.dispatch('validityCheck');
    } catch (_err) {
      // ignore
    }
  }
};

/**
 * Same rule as Sidebar: admin OR account flag can_see_all_conversations.
 * Prefer explicit accountId — during redirects rootState.route may still be empty.
 */
export const canSeeAllConversations = (accountId = null) => {
  const id =
    accountId != null && accountId !== ''
      ? Number(accountId)
      : store.getters.getCurrentAccountId;

  const accounts = store.getters.getCurrentUser?.accounts || [];
  const account =
    accounts.find(a => Number(a.id) === Number(id)) ||
    store.getters.getCurrentAccount ||
    {};

  if (account.role === 'administrator') return true;
  return Boolean(account.can_see_all_conversations);
};

/**
 * Teams the current user belongs to (is_member), ordered like the store (id ASC).
 */
const fetchMyTeamsForAccount = async accountId => {
  const cached = store.getters['teams/getMyTeams'] || [];
  if (cached.length) return cached;

  let teams = [];
  if (accountId) {
    try {
      const { data } = await axios.get(`/api/v1/accounts/${accountId}/teams`);
      teams = Array.isArray(data) ? data : data?.payload || [];
    } catch (_e) {
      teams = [];
    }
  }

  if (!teams.length) {
    try {
      await store.dispatch('teams/get');
    } catch (_e) {
      // ignore
    }
    return store.getters['teams/getMyTeams'] || [];
  }

  return teams.filter(team => team.is_member).sort((a, b) => a.id - b.id);
};

const getFirstTeamForUser = async accountId => {
  const teams = await fetchMyTeamsForAccount(accountId);
  return teams[0] || null;
};

/**
 * Landing folder inside a channel:
 * - with permission → Todas as conversas (inbox_dashboard)
 * - without → first team the user belongs to
 */
export const getPreferredInboxLanding = async (inbox, accountId = null) => {
  if (!inbox?.id) return null;

  await ensureCurrentUserLoaded();

  if (canSeeAllConversations(accountId)) {
    return {
      name: 'inbox_dashboard',
      params: { inbox_id: String(inbox.id) },
    };
  }

  const firstTeam = await getFirstTeamForUser(accountId);
  if (firstTeam?.id) {
    return {
      name: 'inbox_team_conversations',
      params: {
        inbox_id: String(inbox.id),
        teamId: String(firstTeam.id),
      },
    };
  }

  // No team membership: mentions is the remaining inbox-scoped entry
  return {
    name: 'inbox_mentions',
    params: { inbox_id: String(inbox.id) },
  };
};

/**
 * Resolve landing: first sidebar channel + Todas as conversas OR first team.
 */
export const resolveDefaultAccountLanding = async accountId => {
  const id = String(accountId || '');
  if (!id) return null;

  await ensureCurrentUserLoaded();

  const firstInbox = await getFirstSidebarInbox(id);
  if (!firstInbox?.id) {
    return {
      path: `/app/accounts/${id}/settings/inboxes/list`,
    };
  }

  const folder = await getPreferredInboxLanding(firstInbox, id);
  return {
    name: folder.name,
    params: {
      accountId: id,
      ...folder.params,
    },
  };
};

/**
 * /dashboard beforeEnter → first channel folder (permission-aware).
 */
export const redirectHomeToFirstInbox = async to => {
  const accountId = String(to.params.accountId || '');
  try {
    const landing = await resolveDefaultAccountLanding(accountId);
    if (landing?.name) {
      return { ...landing, replace: true };
    }
    if (landing?.path) {
      return { path: landing.path, replace: true };
    }
  } catch (_e) {
    // ignore
  }

  return {
    path: `/app/accounts/${accountId}/settings/inboxes/list`,
    replace: true,
  };
};

export const redirectConversationToItsInbox = async (to, _from, next) => {
  const conversationId = to.params.conversation_id;
  if (!conversationId) {
    next();
    return;
  }

  try {
    const chats = store.getters.getAllConversations || [];
    let chat = chats.find(c => c.id === Number(conversationId));

    if (!chat?.inbox_id) {
      await store.dispatch('getConversation', conversationId);
      chat = (store.getters.getAllConversations || []).find(
        c => c.id === Number(conversationId)
      );
    }

    if (!chat?.inbox_id) {
      const selected = store.getters.getSelectedChat;
      if (selected?.id === Number(conversationId) && selected.inbox_id) {
        chat = selected;
      }
    }

    if (chat?.inbox_id) {
      const destination = {
        name: 'conversation_through_inbox',
        params: {
          accountId: to.params.accountId,
          inbox_id: String(chat.inbox_id),
          conversation_id: conversationId,
        },
        query: to.query,
        replace: true,
      };
      next(destination);
      return;
    }
  } catch (_e) {
    // Fall through
  }

  const landing = await resolveDefaultAccountLanding(to.params.accountId);
  if (landing?.name) {
    const destination = {
      name: landing.name,
      params: {
        ...landing.params,
        conversation_id: conversationId,
      },
      query: to.query,
      replace: true,
    };
    // Prefer conversation_through_* when possible
    if (landing.name === 'inbox_dashboard') {
      destination.name = 'conversation_through_inbox';
      destination.params.conversation_id = conversationId;
    } else if (landing.name === 'inbox_team_conversations') {
      destination.name = 'conversation_through_inbox_team';
      destination.params.conversationId = conversationId;
      delete destination.params.conversation_id;
    }
    next(destination);
    return;
  }

  next();
};

export const redirectAllConversationsIfUnauthorized = async (
  to,
  _from,
  next
) => {
  if (canSeeAllConversations(to.params.accountId)) {
    next();
    return;
  }

  const landing = await getPreferredInboxLanding(
    { id: Number(to.params.inbox_id) },
    to.params.accountId
  );
  if (!landing) {
    next();
    return;
  }

  const destination = {
    name: landing.name,
    params: {
      accountId: to.params.accountId,
      ...landing.params,
      ...(to.params.conversation_id
        ? { conversation_id: to.params.conversation_id }
        : {}),
    },
    replace: true,
  };
  next(destination);
};
